import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import clsx from "clsx";
import { EmptyState, Skeleton, ToneMarker, useApi, useCurrentUser, useT, type Tone } from "@iga/ui";
import { useCampaigns, useTasks, useFmt, firstName } from "../lib/hooks";
import type { AccessView } from "../lib/types";
import { LinkButton, LoadError, NoIdentity, Section, ValidTo } from "../components/common";

interface Row { id: string; tone: Tone; title: string; context: string; to: string }

function RowLink({ r }: { r: Row }) {
  return (
    <li>
      <Link to={r.to} className="flex min-h-14 items-center gap-3 px-4 py-2.5 text-fg no-underline hover:bg-surface-2">
        <span className={clsx("inline-flex h-6 w-6 flex-none items-center justify-center rounded-full", `iga-tone-${r.tone}`)}><ToneMarker tone={r.tone} /></span>
        <span className="min-w-0 flex-1"><span className="block font-semibold">{r.title}</span><span className="block text-small text-fg-3">{r.context}</span></span>
        <ChevronRight className="h-4 w-4 flex-none text-fg-3" aria-hidden="true" />
      </Link>
    </li>
  );
}

/** Home answers "what do I need to do": the inbox summary first, then expiring access and quick actions. */
export function HomePage() {
  const t = useT();
  const fmt = useFmt();
  const me = useCurrentUser();
  const tasks = useTasks();
  const campaigns = useCampaigns(!!me?.identity);
  const access = useApi<AccessView>(["me-access"], "/api/portal/me/access", undefined, { enabled: !!me?.identity });
  const tk = tasks.data;
  const hour = new Date().getHours();
  const greeting = t(hour < 11 ? "p.home.morning" : hour < 17 ? "p.home.afternoon" : "p.home.evening", { name: firstName(me?.identity?.displayName ?? me?.user.displayName) });

  const rows: Row[] = [];
  if (tk) {
    if (tk.detail.requestsFailed) rows.push({ id: "failed", tone: "bad", title: t("p.home.row.failed", { count: tk.detail.requestsFailed }), context: t("p.home.row.failedContext"), to: "/tasks?source=requests" });
    if (tk.detail.escalatedToMe) rows.push({ id: "escalated", tone: "bad", title: t("p.home.row.escalated", { count: tk.detail.escalatedToMe }), context: t("p.home.row.escalatedContext"), to: "/tasks?source=approvals" });
    if (tk.approvals) rows.push({ id: "approvals", tone: tk.detail.approvalsDueSoon ? "warn" : "info", title: t(tk.approvals === 1 ? "p.home.row.approval" : "p.home.row.approvals", { count: tk.approvals }), context: tk.detail.approvalsDueSoon ? t("p.home.row.approvalsDue", { count: tk.detail.approvalsDueSoon }) : t("p.home.row.approvalsContext"), to: "/tasks?source=approvals" });
    for (const c of campaigns.data?.items.filter((c) => c.pending > 0) ?? []) rows.push({ id: c.id, tone: c.overdue ? "warn" : "info", title: t("p.home.row.review", { name: c.name }), context: t("p.home.row.reviewContext", { left: c.pending, date: fmt.day(c.dueAt) }), to: `/review/${c.id}` });
    if (tk.requestsPending) rows.push({ id: "pending", tone: "neutral", title: t(tk.requestsPending === 1 ? "p.home.row.pending" : "p.home.row.pendings", { count: tk.requestsPending }), context: t("p.home.row.pendingContext"), to: "/requests" });
    if (me?.isManager && tk.teamRequests) rows.push({ id: "team", tone: "neutral", title: t("p.home.row.team", { count: tk.teamRequests }), context: t("p.home.row.teamContext"), to: "/requests?scope=forMyReports" });
  }
  const needs = rows.filter((r) => r.tone === "bad" || r.tone === "warn" || r.tone === "info").length;

  return (
    <div className="flex flex-col gap-8">
      <div>
        <h1 className="text-display">{greeting}</h1>
        <p className="mt-1 text-fg-2">{tasks.isLoading ? t("ui.loading") : needs === 0 ? t("p.home.nothingWaiting") : t(needs === 1 ? "p.home.oneThing" : "p.home.things", { count: needs })}</p>
      </div>
      {!me?.identity && <NoIdentity />}
      <LoadError error={tasks.error} onRetry={() => void tasks.refetch()} />

      <Section title={t("p.home.todo")} actions={<Link to="/tasks" className="font-semibold">{t("p.home.allTasks")}</Link>}>
        <div className="rounded-card border border-line bg-surface overflow-hidden">
          {tasks.isLoading ? <div className="p-4"><Skeleton rows={3} /></div>
            : rows.length === 0 ? <EmptyState kind="all-done" title={t("p.home.nothingWaiting")} body={t("p.home.nothingWaitingBody")} />
            : <ul className="divide-y divide-line">{rows.map((r) => <RowLink key={r.id} r={r} />)}</ul>}
        </div>
      </Section>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Section title={t("p.home.expiring")} className="lg:col-span-2">
          <div className="rounded-card border border-line bg-surface overflow-hidden">
            {!me?.identity ? <p className="p-4 text-fg-3">{t("p.noIdentity.short")}</p>
              : access.isLoading ? <div className="p-4"><Skeleton rows={2} /></div>
              : access.error ? <div className="p-4"><LoadError error={access.error} onRetry={() => void access.refetch()} /></div>
              : (access.data?.expiringSoon.length ?? 0) === 0 ? <p className="p-4 text-fg-2">{t("p.home.nothingExpires")}</p>
              : (
                <ul className="divide-y divide-line">
                  {access.data!.expiringSoon.map((x) => (
                    <li key={x.assignmentId} className="flex min-h-14 flex-wrap items-center justify-between gap-2 px-4 py-2.5">
                      <span className="min-w-0"><span className="block font-semibold">{x.displayName}</span><span className="block text-small text-fg-3"><ValidTo value={x.validTo} /></span></span>
                      <LinkButton to={`/my-access?extend=${x.kind}:${x.assignmentId}`}>{t("p.access.extend")}</LinkButton>
                    </li>
                  ))}
                </ul>
              )}
          </div>
        </Section>
        <Section title={t("p.home.quick")}>
          <div className="flex flex-col gap-2">
            <LinkButton to="/request" variant="primary" className="justify-start">{t("p.home.requestForMe")}</LinkButton>
            {me?.isManager && <LinkButton to="/request?for=team" className="justify-start">{t("p.home.requestForTeam")}</LinkButton>}
            <LinkButton to="/my-access" className="justify-start">{t("p.home.seeAccess")}</LinkButton>
            <LinkButton to="/profile" className="justify-start">{t("p.home.setDelegation")}</LinkButton>
          </div>
          {access.data && <p className="mt-3 text-small text-fg-3">{t("p.home.counts", { roles: access.data.roles.length, entitlements: access.data.entitlements.length, accounts: access.data.accounts.length })}</p>}
        </Section>
      </div>
    </div>
  );
}
